import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import Confetti from "@/components/Confetti";
import { Sparkles, Upload, Receipt, Banknote, ArrowRight } from "lucide-react";

export default function Welcome() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  useEffect(() => {
    checkAuth();
    // Auto-forward into the personal control panel after a short celebration
    // so the user lands directly on the dashboard for their active tier.
    const t = setTimeout(() => navigate("/dashboard", { replace: true }), 3200);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid-bg">
      <Confetti duration={6000} density={180} />
      <header className="px-8 py-6 flex items-center justify-between">
        <Logo size="md" />
      </header>
      <div className="max-w-3xl mx-auto px-6 py-14 fade-up">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-semibold uppercase tracking-[0.22em] mb-6">
            <Sparkles size={12} /> Vault Unlocked
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white leading-[1.05]" data-testid="welcome-heading">
            Welcome to Receipt Sheets{user?.name ? `, ${user.name.split(" ")[0]}` : ""}.
          </h1>
          <p className="text-slate-300 mt-5 text-lg">
            Your{" "}
            <span className="text-emerald-300 uppercase tracking-widest font-mono text-sm">
              {user?.subscription_tier || "Pro"}
            </span>{" "}
            subscription is active. Taking you to your control panel…
          </p>
        </div>

        <div className="mt-12 grid sm:grid-cols-3 gap-4 stagger">
          {[
            { icon: Upload, title: "Scan your first receipt", desc: "Drop an image and watch GPT-5.2 fill in every field.", to: "/upload" },
            { icon: Receipt, title: "Browse the vault", desc: "8 demo receipts pre-loaded so you can explore instantly.", to: "/receipts" },
            { icon: Banknote, title: "Match your bank", desc: "See how bank feed reconciliation catches duplicates.", to: "/bank-feed" },
          ].map((c) => (
            <button
              key={c.title}
              onClick={() => navigate(c.to)}
              className="glass rounded-2xl p-6 text-left card-hover group"
            >
              <c.icon size={24} className="text-emerald-400 mb-4" />
              <div className="font-semibold text-white">{c.title}</div>
              <div className="text-slate-400 text-sm mt-1.5">{c.desc}</div>
              <div className="mt-4 flex items-center gap-1.5 text-emerald-400 text-sm font-medium">
                Open <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
              </div>
            </button>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <button
            onClick={() => navigate("/dashboard")}
            data-testid="welcome-enter-btn"
            className="h-14 px-8 rounded-xl bg-emerald-500 text-slate-950 font-semibold text-base hover:bg-emerald-400 transition-colors flex items-center gap-2"
          >
            Enter my dashboard <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
